// Presentation helpers for the Realtime AI Monitor. LiteLLM cost is USD, so these
// are independent of the EUR cost-control money helpers. All time labels are UTC.

function trimZero(s) {
  return s.replace(/\.0$/, "");
}

// usd formats a USD amount compactly: 2dp below $10, whole dollars to <$1k, then k/M.
export function usd(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `$${sign}${trimZero((abs / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `$${sign}${trimZero((abs / 1e3).toFixed(1))}k`;
  if (abs >= 10) return `$${sign}${abs.toFixed(0)}`;
  return `$${sign}${abs.toFixed(2)}`;
}

// compactNumber formats counts (events, tokens) with k/M/B/T scaling. B/T
// exist for the Trends headline KPI (total tokens), which runs into the
// billions in production — below 1e9 this is unchanged from the Realtime
// monitor's original k/M-only behaviour.
export function compactNumber(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e12) return `${sign}${trimZero((abs / 1e12).toFixed(1))}T`;
  if (abs >= 1e9) return `${sign}${trimZero((abs / 1e9).toFixed(1))}B`;
  if (abs >= 1e6) return `${sign}${trimZero((abs / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `${sign}${trimZero((abs / 1e3).toFixed(1))}k`;
  return `${sign}${Math.round(abs)}`;
}

// ageLabel turns "seconds since latest ingest" into a short human string.
export function ageLabel(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// hourLabel formats an ISO hour as UTC "HH:00".
export function hourLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getUTCHours()).padStart(2, "0")}:00`;
}

// clockTime formats an ISO timestamp as UTC "HH:MM:SS".
export function clockTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (x) => String(x).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

// eur formats a EUR amount with the same compact contract as usd — used for the AI cost-basis
// proof tile (subscription-fee re-basis figures), which is EUR regardless of usd() above (that
// one is the LiteLLM-native per-call cost, always USD).
export function eur(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `€${sign}${trimZero((abs / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `€${sign}${trimZero((abs / 1e3).toFixed(1))}k`;
  if (abs >= 10) return `€${sign}${abs.toFixed(0)}`;
  return `€${sign}${abs.toFixed(2)}`;
}

// aiCostBasis returns the current month's re-basis figures — actual (re-based) cost, the
// API-equivalent (shadow) value, the modelled saving between them, and the saving as a percent —
// or null when there is nothing to compare. shadow<=0 means no AI usage (or no plan configured)
// for the period, so callers render a neutral placeholder rather than "€0 · €0 · 0%"/NaN. The
// backend's effective_discount_pct is a fraction (e.g. 0.8246 for 82%, see GET
// /api/v1/ai-cost/summary), scaled to a whole percent here.
export function aiCostBasis(aiCost) {
  if (!aiCost || !(Number(aiCost.shadow) > 0)) return null;
  const actual = Number(aiCost.actual) || 0;
  const shadow = Number(aiCost.shadow) || 0;
  const pct = Math.round((Number(aiCost.effective_discount_pct) || 0) * 100);
  return { actual, shadow, savings: Math.max(0, shadow - actual), pct };
}

// aiCostBasisLabel renders the re-basis as a savings-oriented one-liner:
// "Estimated savings vs API-equivalent pricing: €940 (82%)". Reframed from the prior "effective
// discount" wording, which could read like a contracted vendor discount rather than the modelled
// saving a flat subscription delivers against standard provider API list pricing.
export function aiCostBasisLabel(aiCost) {
  const b = aiCostBasis(aiCost);
  if (!b) return "No AI usage this period";
  return `Estimated savings vs API-equivalent pricing: ${eur(b.savings)} (${b.pct}%)`;
}

// monitorVerdict derives the decision-oriented banner from a monitor summary plus the month's cost
// basis. The headline is the single most decision-relevant fact — a data-quality warning when
// ingestion has gone quiet (that overrides everything: the numbers below may be wrong), otherwise
// the financial story. `detail` is a supporting sentence combining current activity, financial
// interpretation, and freshness; `tone` is "warning" (amber) only when a freshness threshold is
// actually breached. Today's operational figures are the LiteLLM meter (USD); the re-basis is the
// EUR subscription — each is scoped by timeframe in the copy so the two currencies don't read as
// an inconsistency.
export function monitorVerdict(summary = {}, aiCost) {
  const totals = summary.totals || {};
  const freshness = summary.freshness || {};
  const hasEvents = freshness.latest_ingest_ts != null;
  const flowing = !!freshness.flowing;
  const stale = hasEvents && !flowing;
  const age = ageLabel(freshness.seconds_since_latest);
  const basis = aiCostBasis(aiCost);

  let tone = "neutral";
  let headline;
  if (stale) {
    tone = "warning";
    headline = `Ingestion may be stale — last event ${age}`;
  } else if (!hasEvents) {
    headline = "No AI events received today";
  } else if (basis) {
    headline = `Subscription plan saving ~${basis.pct}% vs API-equivalent pricing`;
  } else {
    headline = "AI ingestion is healthy";
  }

  let detail = hasEvents
    ? `${usd(totals.cost)} metered across ${compactNumber(totals.events)} events and ${compactNumber(totals.tokens)} tokens today.`
    : "No events ingested yet today.";
  if (basis) {
    detail += ` This month, subscription usage is saving an estimated ${eur(basis.savings)} (${basis.pct}%) versus API-equivalent pricing.`;
  }
  // Freshness fact only when the headline doesn't already carry it (stale headline states the age).
  if (!stale && hasEvents) {
    detail += ` Last event received ${age}.`;
  }

  return { tone, headline, detail };
}
